import React, { CSSProperties, useCallback } from 'react';
import { ProductOption } from '../../../../apis/models/products/res/ProductOption';
import styled from 'styled-components';
import Typo from '../../../../components/atom/Typo';
import colors from '../../../../constants/colors.constants';
import Checkbox from '../../../../components/atom/Checkbox';
import { formatCurrency } from '../../../../utils/utils';
import HighLighter from 'react-highlight-words';
import strings from '../../../../constants/strings.constants';
import { EventTag } from './FilterItem';

export interface ProductOptionItemProps {
  productOption: ProductOption;
  selected?: boolean;
  onClick?: (
    productOption: ProductOption,
    isTutProductOption?: boolean,
  ) => void;
  searchKeyword?: string;
  isEvent?: boolean;
  tutorialProduct?: boolean;
  customLabels?: {
    zeroPriceLabel?: string;
    needConsultationLabel?: string;
  };
}
function ProductOptionItem(props: ProductOptionItemProps) {
  const {
    isEvent = false,
    productOption,
    selected = false,
    onClick,
    searchKeyword = '',
    tutorialProduct = false,
    customLabels,
  } = props;
  const {
    productOptionName,
    productOptionDescription,
    id,
    productOptionTotalDiscountPrice,
    productOptionTotalPrice,
  } = productOption;

  const getDiscountRate = useCallback(() => {
    if (productOptionTotalDiscountPrice && productOptionTotalPrice) {
      const discountRate = Math.round(
        100 - (productOptionTotalDiscountPrice / productOptionTotalPrice) * 100,
      );
      return `${discountRate}%`;
    }
    return '';
  }, [productOptionTotalPrice, productOptionTotalDiscountPrice]);

  const getProductOptionOriginPriceText = useCallback(() => {
    if (productOptionTotalDiscountPrice && productOptionTotalPrice) {
      return `${formatCurrency(productOptionTotalPrice)}${strings.priceUnit}`;
    }
    return '';
  }, [productOptionTotalDiscountPrice, productOptionTotalPrice]);

  const getProductOptionPriceText = useCallback(() => {
    if (productOptionTotalDiscountPrice) {
      const price = formatCurrency(productOptionTotalDiscountPrice);
      if (customLabels?.zeroPriceLabel && price == '0') {
        return customLabels?.zeroPriceLabel;
      }

      return `${formatCurrency(productOptionTotalDiscountPrice)}${strings.priceUnit}`;
    }

    if (productOptionTotalPrice) {
      const price = formatCurrency(productOptionTotalPrice);
      if (customLabels?.zeroPriceLabel && price == '0') {
        return customLabels?.zeroPriceLabel;
      }

      return `${formatCurrency(productOptionTotalPrice)}${strings.priceUnit}`;
    }

    if (
      customLabels?.zeroPriceLabel &&
      (productOptionTotalPrice == 0 || productOptionTotalDiscountPrice == 0)
    ) {
      return customLabels.zeroPriceLabel;
    }

    if (customLabels?.needConsultationLabel) {
      return customLabels.needConsultationLabel;
    }

    return strings.needConsultation;
  }, [
    productOptionTotalDiscountPrice,
    productOptionTotalPrice,
    customLabels?.needConsultationLabel,
    customLabels?.zeroPriceLabel,
  ]);

  const originPriceText = getProductOptionOriginPriceText();
  const priceText = getProductOptionPriceText();
  const discountRate = getDiscountRate();

  const highlightStyle: CSSProperties = {
    fontWeight: 700,
    color: 'initial',
  };

  return (
    <ProductOptionWrapper
      className={tutorialProduct ? 'tutorial-product' : undefined}
      $selected={selected}
      onClick={() => {
        if (onClick) {
          onClick(productOption, tutorialProduct);
        }
      }}
    >
      <div className="flex items-center gap-3">
        <div>
          <Checkbox id={`${id}`} checked={selected} />
        </div>

        <div className="flex flex-wrap gap-1 items-center">
          <Typo
            type="r-16"
            style={{
              lineHeight: '20px',
            }}
          >
            <HighLighter
              autoEscape
              highlightStyle={highlightStyle}
              textToHighlight={productOptionName}
              searchWords={[searchKeyword]}
            >
              {productOptionName}
            </HighLighter>
            {isEvent && (
              <EventTag
                style={{
                  marginLeft: 4,
                }}
              >
                {strings.event}
              </EventTag>
            )}
          </Typo>
        </div>
      </div>
      <div className="flex flex-col gap-1 ml-[36px] mt-1">
        {productOptionDescription && (
          <div>
            <Typo type="r-12" color={colors.subTitle}>
              {productOptionDescription}
            </Typo>
          </div>
        )}
        {originPriceText && (
          <div>
            <Typo
              type="r-12"
              color={colors.subTitle}
              style={{
                textDecoration: 'line-through',
              }}
            >
              {originPriceText}
            </Typo>
          </div>
        )}
        <div className="flex gap-1">
          {discountRate && (
            <Typo type="b-14" color={colors.red2}>
              {discountRate}
            </Typo>
          )}
          <Typo type="b-14" color={colors.subTitle}>
            {priceText}
          </Typo>
        </div>
      </div>
    </ProductOptionWrapper>
  );
}

export default ProductOptionItem;

const ProductOptionWrapper = styled.div<{ $selected: boolean }>`
  padding: 14px 16px;
  background-color: ${(props) => {
    if (props.$selected) return colors.greyHover;
    return 'white';
  }};
  transition: background-color 0.2s;
  cursor: pointer;
`;
